import { searchRagas } from '../api/seed.js';
import type { RagaRecord } from '@swarasense/shared';
import { createCombobox } from './combobox.js';
import type { PopoverDeps } from './settings-popover.js';

/** The Settings/Sa trigger anchors this row builds — everything `app.ts`
 * needs to merge in the transport's Thaalam trigger anchors, the four panel
 * elements it mounts itself (in the top-level `.popover-layer`, see app.ts),
 * and the shared scrim, then mount all four popovers together via
 * `settings-popover.ts`'s `mountPopovers` (metronome controls regroup: that
 * call now lives in `app.ts`, the one module that owns the navbar, the
 * controls row, the stage, AND the popover layer, rather than here).
 * Frontend-redesign-v2: this row builds only the TRIGGER buttons, not their
 * panels — the panels are `position: fixed` viewport-anchored sheets that
 * must not be nested inside `.navbar`/`.controls-row` (whose `backdrop-
 * filter`, if either ever grew one, would otherwise become their fixed-
 * positioning containing block — see styles.css), so `app.ts` mounts them
 * itself, as siblings of `.app`. */
export interface ControlsRowAnchors {
  settingsBtn: HTMLButtonElement;
  saBtn: HTMLButtonElement;
  saLabel: HTMLElement;
}

/** Raga name + aliases come from our own seed data (never user input), so plain
 * interpolation into the combobox option markup is fine — same trust boundary
 * the pre-Phase-4b setup screen used for the same data. */
function ragaOptionHtml(record: RagaRecord): string {
  const badge =
    record.type === 'melakarta'
      ? `Melakarta #${record.melakarta_number}`
      : `Janya${record.parent_melakarta !== null ? ` · #${record.parent_melakarta}` : ''}`;
  return `<span>${record.name}</span><span class="opt-meta">${badge}</span>`;
}

/**
 * UI reimagine (Task 2): the slim controls row that sits directly beneath
 * the persistent navbar — `Ragam ▾ · Sa · Tanpura · ⚙ Settings`, exactly the
 * approved mockup's practice-page control strip. This module (renamed from
 * the pre-reimagine `topbar.ts`, which used to also render the wordmark and
 * the theme toggle — both moved up into the new shared `navbar.ts` (and its
 * content-page counterpart, `skeleton.ts`'s `renderHeader()`))
 * owns the ragam combobox (reuses `combobox.ts` + `searchRagas`, same data
 * source the old setup screen used), the Sa popover trigger, the Tanpura
 * toggle + its options caret (moved here from the stage's transport — the
 * OTHER transport controls, Thaalam/Ear training/Start listening, stay in
 * the stage; only Tanpura joined the mockup's controls row), and the
 * Settings popover trigger.
 *
 * `root` is the `<div class="controls-row">` element itself (see `app.ts`) —
 * this only fills its children. Mounted once and never unmounted, same
 * lifetime reasoning as `navbar.ts`'s `mountNavbar`.
 *
 * This module only owns the row's own markup (ragam combobox, the three
 * popover/toggle TRIGGER elements) and returns the Settings/Sa anchors —
 * frontend-redesign-v2 moved the popover PANELS themselves (and the shared
 * scrim) out into `app.ts`'s top-level `.popover-layer`, since a `fixed`-
 * positioned panel must not be a descendant of a blurred/transformed
 * ancestor. `app.ts` merges this row's trigger anchors with the transport's
 * Thaalam trigger anchor and its own panel/scrim elements, then calls
 * `settings-popover.ts`'s `mountPopovers` once for all four, which fills
 * every panel and wires their open/close mechanics and live-apply behavior —
 * the same split `combobox.ts` already has with this file for the ragam
 * field. Tanpura's own start/stop click handler (NOT a popover — the button
 * itself starts/stops the drone) is wired by `app.ts`, exactly as it was
 * when this markup lived in the stage's transport; only the button's
 * LOCATION in the DOM moved, not its id or its behavior.
 */
export function mountControlsRow(root: HTMLElement, deps: PopoverDeps): ControlsRowAnchors {
  const { store } = deps;
  const state = store.get();

  root.innerHTML = `
    <div class="ctrl raga combo">
      <span class="lbl">Ragam</span>
      <input id="raga-input" class="raga-input" type="text" autocomplete="off" value="${state.ragaName}" />
      <ul id="raga-listbox" class="combobox-list"></ul>
    </div>
    <div class="anchor">
      <button class="ctrl" id="sa-btn" type="button" aria-controls="sa-popover">
        <span class="lbl">Sa</span><span id="sa-label">${state.pitchClass}${state.octave}</span>
      </button>
    </div>
    <div class="anchor tgroup">
      <button class="btn" id="drone-btn" type="button" aria-pressed="false"><span class="g"></span>Tanpura</button>
      <button class="btn caret" id="drone-caret" type="button" aria-label="Tanpura options" aria-controls="drone-popover">⋯</button>
    </div>
    <div class="anchor">
      <button class="ctrl" id="settings-btn" type="button" aria-controls="settings-popover">⚙ Settings</button>
    </div>`;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  // Never aborted: this row is mounted exactly once for the page's whole
  // lifetime (no screen to unmount it on), so there's nothing to clean up.
  const { signal } = new AbortController();

  createCombobox<RagaRecord>({
    input: $<HTMLInputElement>('raga-input'),
    listEl: $('raga-listbox'),
    signal,
    ariaLabel: 'Search for a ragam by name or alias',
    placeholder: 'Search ragam…',
    search: (query) => searchRagas(query, 25),
    label: (record) => record.name,
    renderOption: ragaOptionHtml,
    onSelect: (record) => store.set({ ragaName: record.name }),
  });

  return {
    settingsBtn: $<HTMLButtonElement>('settings-btn'),
    saBtn: $<HTMLButtonElement>('sa-btn'),
    saLabel: $('sa-label'),
  };
}
